<template>
    <div
        v-if="organisations.length === 1"
        class="organisation-switcher organisation-switcher-static"
        data-cy="organisationSwitcher"
        role="group"
        :aria-label="`Current organisation: ${activeOrganisation?.name ?? ''}`"
    >
        <span
            class="organisation-avatar"
            :style="{
                backgroundColor:
                    organisationStore.getActiveOrganisationColour ?? undefined,
            }"
            aria-hidden="true"
        >
            {{ getOrganisationInitials(activeOrganisation?.name ?? "") }}
        </span>
        <span class="organisation-switcher-text">
            <span
                class="organisation-switcher-name body-medium white-text"
                data-cy="activeOrganisationName"
            >
                {{ activeOrganisation?.name }}
            </span>
        </span>
    </div>
    <v-menu v-else-if="organisations.length > 1" location="bottom start">
        <template #activator="{ props }">
            <v-btn
                v-bind="props"
                class="organisation-switcher"
                data-cy="organisationSwitcher"
                :disabled="organisationStore.isSwitching"
                variant="text"
                :aria-label="`Switch organisation, current: ${activeOrganisation?.name ?? ''}`"
            >
                <span
                    class="organisation-avatar"
                    :style="{
                        backgroundColor:
                            organisationStore.getActiveOrganisationColour ??
                            undefined,
                    }"
                    aria-hidden="true"
                >
                    {{
                        getOrganisationInitials(activeOrganisation?.name ?? "")
                    }}
                </span>
                <span class="organisation-switcher-text">
                    <span
                        class="organisation-switcher-label body-small secondary-text"
                        aria-hidden="true"
                    >
                        Select organisation
                    </span>
                    <span
                        class="organisation-switcher-name body-medium white-text"
                        data-cy="activeOrganisationName"
                    >
                        {{ activeOrganisation?.name }}
                    </span>
                </span>
                <v-icon :icon="mdiChevronDown" size="small" />
            </v-btn>
        </template>
        <v-list class="organisation-menu" data-cy="organisationOptions">
            <v-list-item
                v-for="organisation in organisations"
                :key="organisation.shortcode"
                class="organisation-menu-item"
                data-cy="organisationOption"
                :active="organisation.shortcode === activeShortcode"
                :disabled="organisationStore.isSwitching"
                @click="switchOrganisation(organisation.shortcode)"
            >
                <template #prepend>
                    <span
                        class="organisation-avatar mr-3"
                        :style="{
                            backgroundColor: colourFor(organisation.shortcode),
                        }"
                        aria-hidden="true"
                    >
                        {{ getOrganisationInitials(organisation.name) }}
                    </span>
                </template>
                <v-list-item-title class="body-medium white-text">
                    {{ organisation.name }}
                </v-list-item-title>
                <template #append>
                    <v-icon
                        v-if="organisation.shortcode === activeShortcode"
                        :icon="mdiCheck"
                        size="small"
                        class="mr-2"
                    />
                    <v-tooltip location="top">
                        <template #activator="{ props: tooltipProps }">
                            <v-btn
                                v-bind="tooltipProps"
                                data-cy="organisationFavouriteButton"
                                variant="text"
                                size="small"
                                density="comfortable"
                                :icon="
                                    isFavourite(organisation.shortcode)
                                        ? mdiStar
                                        : mdiStarOutline
                                "
                                :class="{
                                    'favourite-active': isFavourite(
                                        organisation.shortcode,
                                    ),
                                }"
                                :aria-label="
                                    isFavourite(organisation.shortcode)
                                        ? `Remove ${organisation.name} as favourite`
                                        : `Set ${organisation.name} as favourite`
                                "
                                :aria-pressed="
                                    isFavourite(organisation.shortcode)
                                "
                                @click.stop="
                                    toggleFavourite(organisation.shortcode)
                                "
                            />
                        </template>
                        <span data-cy="organisationFavouriteTooltip">
                            {{
                                isFavourite(organisation.shortcode)
                                    ? "Default when you sign in. Click to remove."
                                    : "Set as default organisation when you sign in."
                            }}
                        </span>
                    </v-tooltip>
                </template>
            </v-list-item>
        </v-list>
    </v-menu>
</template>

<script setup lang="ts">
import { computed, watch } from "vue";
import { usePiniaStore } from "@/stores/store";
import { useOrganisationStore } from "@/stores/organisationStore";
import { switchActiveOrganisation } from "@/methods/organisationSwitching";
import {
    getOrganisationColour,
    getOrganisationInitials,
} from "@/methods/organisationColour";
import { mdiCheck, mdiChevronDown, mdiStar, mdiStarOutline } from "@mdi/js";
import { useRouter } from "vue-router";

const router = useRouter();
const pStore = usePiniaStore();
const organisationStore = useOrganisationStore();

const organisations = computed(() =>
    [...organisationStore.getAvailableOrganisations].sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
    ),
);
const shortcodes = computed(() =>
    organisations.value.map(({ shortcode }) => shortcode),
);
const activeShortcode = computed(
    () => organisationStore.getActiveOrganisationShortcode,
);
const activeOrganisation = computed(
    () => organisationStore.getActiveOrganisation,
);

const colourFor = (shortcode: string) =>
    getOrganisationColour(shortcode, shortcodes.value);

const isFavourite = (shortcode: string) =>
    organisationStore.favouriteOrganisationShortcode === shortcode;

const toggleFavourite = (shortcode: string) => {
    organisationStore.toggleFavouriteOrganisation(
        pStore.getUserEmail,
        shortcode,
    );
};

const switchOrganisation = async (shortcode: string) => {
    if (organisationStore.isSwitching) return;

    await switchActiveOrganisation(shortcode, router);
};

// Favourite state isn't persisted in session storage, so reload it after a page refresh.
watch(
    () => pStore.getUserEmail,
    (email) => organisationStore.loadFavouriteOrganisation(email),
    { immediate: true },
);
</script>
